import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setFilter,
  setFilterValue,
  setFilterEnabled,
  setFilterLoading,
  selectAllFilters,
  selectFilter,
  selectFilterValue,
  selectFilterLabel,
  selectFilterEnabled,
  selectFilterLoading,
  selectFilterOptions,
  selectEnabledFilters,
} from '../../store/slices/filterSliceV2';

/**
 * Custom hook for managing filter state using Filter Slice V2
 *
 * Provides easy access to filter actions and selectors
 *
 * @returns {Object} Hook methods and state accessors
 */
export const useFilterV2 = () => {
  const dispatch = useDispatch();

  // Actions - memoized to prevent infinite loops in useCallback dependencies
  const updateFilter = React.useCallback(
    (filterId, selectedValue, enabled) => {
      dispatch(setFilter({ filterId, selectedValue, enabled }));
    },
    [dispatch]
  );

  const updateFilterValue = React.useCallback(
    (filterId, selectedValue, selectedLabel) => {
      dispatch(setFilterValue({ filterId, selectedValue, selectedLabel }));
    },
    [dispatch]
  );

  const updateFilterEnabled = React.useCallback(
    (filterId, enabled) => {
      dispatch(setFilterEnabled({ filterId, enabled }));
    },
    [dispatch]
  );

  const updateFilterLoading = React.useCallback(
    (filterId, loading) => {
      dispatch(setFilterLoading({ filterId, loading }));
    },
    [dispatch]
  );

  // Selector hooks
  const useAllFilters = () => useSelector(selectAllFilters);

  const useFilter = (filterId) =>
    useSelector((state) => selectFilter(state, filterId));

  const useFilterValue = (filterId) =>
    useSelector((state) => selectFilterValue(state, filterId));

  const useFilterEnabled = (filterId) =>
    useSelector((state) => selectFilterEnabled(state, filterId));

  const useFilterLoading = (filterId) =>
    useSelector((state) => selectFilterLoading(state, filterId));

  const useEnabledFilters = () => useSelector(selectEnabledFilters);

  return {
    // Actions
    updateFilter,
    updateFilterValue,
    updateFilterEnabled,
    updateFilterLoading,

    // Selector hooks
    useAllFilters,
    useFilter,
    useFilterValue,
    useFilterEnabled,
    useFilterLoading,
    useEnabledFilters,
  };
};

/**
 * Hook for a specific filter - provides all methods for a single filter
 *
 * @param {string} filterId - The unique identifier for the filter
 * @returns {Object} Filter-specific methods and state
 */
export const useSpecificFilter = (filterId) => {
  const dispatch = useDispatch();

  // Get current filter state
  const filter = useSelector((state) => selectFilter(state, filterId));
  const selectedValue = useSelector((state) =>
    selectFilterValue(state, filterId)
  );
  const selectedLabel = useSelector((state) =>
    selectFilterLabel(state, filterId)
  );
  const enabled = useSelector((state) => selectFilterEnabled(state, filterId));
  const loading = useSelector((state) => selectFilterLoading(state, filterId));
  const options = useSelector((state) => selectFilterOptions(state, filterId));

  // Filter-specific actions - memoized to prevent infinite loops
  const setValue = React.useCallback(
    (selectedValue, selectedLabel) => {
      dispatch(setFilterValue({ filterId, selectedValue, selectedLabel }));
    },
    [dispatch, filterId]
  );

  const setEnabled = React.useCallback(
    (enabled) => {
      dispatch(setFilterEnabled({ filterId, enabled }));
    },
    [dispatch, filterId]
  );

  const setLoading = React.useCallback(
    (loading) => {
      dispatch(setFilterLoading({ filterId, loading }));
    },
    [dispatch, filterId]
  );

  const updateBoth = React.useCallback(
    (selectedValue, enabled, selectedLabel) => {
      dispatch(setFilter({ filterId, selectedValue, enabled, selectedLabel }));
    },
    [dispatch, filterId]
  );

  const setOptions = React.useCallback(
    (options) => {
      dispatch(setFilter({ filterId, options }));
    },
    [dispatch, filterId]
  );

  return {
    // Current state
    filter,
    selectedValue,
    selectedLabel,
    enabled,
    loading,
    options,

    // Actions
    setValue,
    setEnabled,
    setLoading,
    updateBoth,
    setOptions,
  };
};

export default useFilterV2;
